import isEqual from 'lodash/isEqual';
import zip from 'lodash/zip';

import { RecipientPhoneInfo, SharedUser } from '../../../types/types';
import {
    displayUserToBE,
    roleToPrimaryData,
    SharedUserToDisplay,
} from '../../../stores';

export const filterDeletedUsers = (
    changedUsers: SharedUserChanges[],
    users: SharedUserToDisplay[],
): SharedUserToDisplay[] => {
    const deletedUserIds = new Set<string>(changedUsers.map((item) => item.deleted && item.userId));
    return users.filter(({ userId }) => !deletedUserIds.has(userId));
};

export type SharedUserChanges = Required<Pick<SharedUser, 'userId' | 'email'>>
    & Partial<Pick<SharedUser, 'readOnly' | 'actions' | 'deleted'>>;

const getPrimaryDataChanges = (
    newUser: SharedUserToDisplay,
): SharedUserChanges => {
    const { readOnly, actions } = roleToPrimaryData(newUser.recipientRole);
    return {
        userId: newUser.userId,
        email: newUser.email,
        readOnly,
        actions,
    };
};

export const getChangedUsers = (
    newUsers: SharedUserToDisplay[],
    originalUsers: SharedUserToDisplay[],
): SharedUserChanges[] => (
    (zip(newUsers, originalUsers) as [SharedUserToDisplay | undefined, SharedUserToDisplay | undefined][])
        .reduce<SharedUserChanges[]>((changes, [newUser, originalUser]) => {
            // zip pads with undefined when lengths differ (e.g. ShareModal users [] before fetch completes).
            if (!newUser || !originalUser) {
                return changes;
            }
            if (newUser.deleted) {
                changes.push({ userId: newUser.userId, deleted: true, email: newUser.email });
            } else if (newUser.recipientRole !== originalUser.recipientRole) {
                changes.push(getPrimaryDataChanges(newUser));
            }
            return changes;
        }, [])
);

export const prepareNewUsersList = (
    changedUsers: SharedUserChanges[],
    users: SharedUserToDisplay[],
): SharedUser[] => (
    filterDeletedUsers(changedUsers, users)
        .map<SharedUser>(displayUserToBE)
);

export const checkPhonesChanges = (
    originalList: SharedUserToDisplay[],
    phonesMap: Map<string, RecipientPhoneInfo>,
): boolean => (
    originalList.some(({ email }) => {
        const phoneInfo = phonesMap.get(email);
        if (phoneInfo?.phonesVector) {
            const { currentPhone, initSnapshot } = phoneInfo.phonesVector;
            return !isEqual(currentPhone, initSnapshot);
        }
        return false;
    })
);
